import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { gsap, ScrollTrigger } from "@/animations/gsap";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { deletePhoto, fetchPhotos, type PhotoItem } from "@/lib/api";

const TOTAL_SLOTS = 6;

export function PhotoGallery() {
  const [photos, setPhotos] = useState<PhotoItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  const load = useCallback(async () => {
    try {
      setPhotos(await fetchPhotos());
      setLoadError(false);
    } catch {
      setLoadError(true);
      setPhotos([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (photos !== null) ScrollTrigger.refresh();
  }, [photos]);

  useEffect(() => {
    if (reduced || !photos?.length) return undefined;
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".photo-tile").forEach((el, i) => {
        gsap.fromTo(
          el.querySelector(".photo-media"),
          { clipPath: "inset(0% 0% 100% 0%)" },
          { clipPath: "inset(0% 0% 0% 0%)", duration: 1.1, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%" } }
        );
        gsap.fromTo(
          el.querySelector("img"),
          { scale: 1.18 },
          { scale: 1, duration: 1.4, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%" } }
        );
        gsap.to(el, {
          yPercent: i % 2 === 0 ? -2.5 : 2.5,
          ease: "none",
          scrollTrigger: { trigger: gridRef.current, start: "top bottom", end: "bottom top", scrub: true },
        });
      });
    }, gridRef);
    return () => ctx.revert();
  }, [reduced, photos]);

  const handleDelete = async (id: string) => {
    if (confirmId !== id) {
      setConfirmId(id);
      window.setTimeout(() => setConfirmId((cur) => (cur === id ? null : cur)), 3500);
      return;
    }
    setConfirmId(null);
    try {
      await deletePhoto(id);
      await load();
    } catch (err) {
      console.error("[photo-delete] failed:", err);
      setActionError(err instanceof Error ? `Delete failed: ${err.message}` : "Delete failed.");
    }
  };

  const count = photos?.length ?? 0;
  const placeholderCount = photos === null ? 0 : Math.max(0, TOTAL_SLOTS - count);

  return (
    <div className="mt-16" data-testid="photo-gallery">
      <div>
        <p className="eyebrow">IN FRAME — PERSONAL</p>
        <h3 className="font-display mt-4 text-2xl font-semibold tracking-tight">BEHIND THE KEYBOARD</h3>
      </div>

      {loadError && (
        <p className="mt-8 font-mono text-xs tracking-wider text-muted" data-testid="photo-load-error">
          PHOTOS ARE UNAVAILABLE RIGHT NOW.
        </p>
      )}

      {actionError && (
        <p className="mt-4 font-mono text-xs text-red-400" data-testid="photo-action-error">
          {actionError}
        </p>
      )}

      <div ref={gridRef} className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="photo-grid">
        {photos === null
          ? Array.from({ length: 3 }).map((_, i) => (
              <div className="aspect-[4/5] animate-pulse border border-line bg-surface" key={i} />
            ))
          : (
            <>
              {photos.map((photo, i) => (
                <figure
                  key={photo.id}
                  className={`photo-tile group relative overflow-hidden border border-line bg-surface ${
                    i === 0 ? "aspect-[4/5] sm:col-span-2 sm:row-span-2 sm:aspect-auto" : "aspect-[4/5]"
                  }`}
                  data-testid={`photo-tile-${photo.id}`}
                >
                  <div className="photo-media absolute inset-0 overflow-hidden">
                    <img src={photo.url} alt={photo.caption || `Portrait photo ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  {photo.caption ? (
                    <figcaption className="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent p-4 font-mono text-[10px] tracking-[0.2em] transition-transform duration-500 group-hover:translate-y-0">
                      {photo.caption.toUpperCase()}
                    </figcaption>
                  ) : null}
                  <button
                    className={`absolute right-3 top-3 z-10 flex h-8 items-center justify-center border px-2 font-mono text-[9px] tracking-[0.15em] transition ${
                      confirmId === photo.id
                        ? "bg-black/80"
                        : "h-8 w-8 bg-black/60 opacity-0 group-hover:opacity-100"
                    }`}
                    style={{ borderColor: confirmId === photo.id ? "var(--accent)" : undefined, color: confirmId === photo.id ? "var(--foreground)" : undefined }}
                    onClick={() => void handleDelete(photo.id)}
                    aria-label={confirmId === photo.id ? `Confirm delete photo ${i + 1}` : `Delete photo ${i + 1}`}
                    data-testid={`photo-delete-${photo.id}`}
                  >
                    {confirmId === photo.id ? "DELETE?" : <X size={14} aria-hidden="true" />}
                  </button>
                </figure>
              ))}
              {Array.from({ length: placeholderCount }).map((_, i) => (
                <div
                  key={`placeholder-${i}`}
                  className="flex aspect-[4/5] flex-col items-center justify-center gap-3 border border-dashed border-line bg-surface/50 p-6 text-center"
                  data-testid={`photo-placeholder-${i}`}
                >
                  <ImagePlus size={20} className="text-muted" aria-hidden="true" />
                  <p className="font-mono text-[10px] tracking-[0.25em] text-muted">YOUR PHOTO HERE</p>
                </div>
              ))}
            </>
          )}
      </div>
    </div>
  );
}
